/**
 * Estate Legacy Contacts Hooks
 *
 * React Query hooks for legacy contact CRUD and permission configuration.
 *
 * @module hooks/useEstateLegacyContacts
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@xanectech/auth';
import {
  EstateLegacyContactService,
  type CreateLegacyContactPayload,
  type UpdateLegacyContactPayload,
} from '../services/EstateLegacyContactService';
import {
  EstateContactDocumentWorkflowService,
  type DocWorkflowStatusSnapshot,
  type ContactRole,
} from '../services/EstateContactDocumentWorkflowService';
import type { Database } from '@xanectech/types';
import { createLogger } from '@xanectech/lib';
import { estateLockerKeys } from './useEstateLocker';

const logger = createLogger('useEstateLegacyContacts');

type ContactPermissionInsert =
  Database['public']['Tables']['estate_vault_contact_permissions']['Insert'];

// ============================================
// Query keys
// ============================================

export const legacyContactKeys = {
  list: (userId: string) => ['estate', 'legacyContacts', userId] as const,
  permissions: (userId: string, contactId: string) =>
    ['estate', 'permissions', userId, contactId] as const,
  workflow: (userId: string, contactId: string) =>
    ['estate', 'legacyContact', 'workflow', userId, contactId] as const,
};

// ============================================
// Hooks
// ============================================

/**
 * List active legacy contacts for the authenticated user.
 */
export function useEstateLegacyContacts() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: legacyContactKeys.list(userId),
    queryFn: () => EstateLegacyContactService.listLegacyContacts(userId),
    enabled: !!userId,
  });
}

/**
 * Create a new legacy contact.
 */
export function useCreateLegacyContact() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: (payload: CreateLegacyContactPayload) =>
      EstateLegacyContactService.createLegacyContact(userId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: legacyContactKeys.list(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.overview(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to create legacy contact', {
        context: 'useCreateLegacyContact',
        error: String(error),
      });
    },
  });
}

/**
 * Update an existing legacy contact.
 */
export function useUpdateLegacyContact() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: ({
      contactId,
      payload,
    }: {
      contactId: string;
      payload: UpdateLegacyContactPayload;
    }) =>
      EstateLegacyContactService.updateLegacyContact(userId, contactId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: legacyContactKeys.list(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to update legacy contact', {
        context: 'useUpdateLegacyContact',
        error: String(error),
      });
    },
  });
}

/**
 * Revoke a legacy contact designation.
 */
export function useRevokeLegacyContact() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: ({
      contactId,
      reason,
    }: {
      contactId: string;
      reason: string;
    }) =>
      EstateLegacyContactService.revokeLegacyContact(userId, contactId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: legacyContactKeys.list(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.overview(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to revoke legacy contact', {
        context: 'useRevokeLegacyContact',
        error: String(error),
      });
    },
  });
}

/**
 * List permissions configured for a specific contact.
 */
export function useContactPermissions(contactId: string) {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: legacyContactKeys.permissions(userId, contactId),
    queryFn: () =>
      EstateLegacyContactService.listContactPermissions(userId, contactId),
    enabled: !!userId && !!contactId,
  });
}

/**
 * Upsert a contact permission entry.
 */
export function useUpsertContactPermission(contactId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: (
      payload: Omit<ContactPermissionInsert, 'user_id' | 'contact_id'>,
    ) =>
      EstateLegacyContactService.upsertContactPermission(userId, {
        ...payload,
        contact_id: contactId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: legacyContactKeys.permissions(userId, contactId),
      });
    },
    onError: (error) => {
      logger.error('Failed to upsert contact permission', {
        context: 'useUpsertContactPermission',
        error: String(error),
      });
    },
  });
}

/**
 * Delete a contact permission entry.
 */
export function useDeleteContactPermission(contactId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: (permissionId: string) =>
      EstateLegacyContactService.deleteContactPermission(userId, permissionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: legacyContactKeys.permissions(userId, contactId),
      });
    },
    onError: (error) => {
      logger.error('Failed to delete contact permission', {
        context: 'useDeleteContactPermission',
        error: String(error),
      });
    },
  });
}

/**
 * Evaluate (or re-evaluate) the document workflow status for a legacy contact.
 * Persists the result into access_scope.doc_workflow and invalidates the list.
 */
export function useEvaluateLegacyContactDocWorkflow() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: ({
      contactId,
      countryCode,
    }: {
      contactId: string;
      countryCode: string | null;
    }): Promise<DocWorkflowStatusSnapshot> =>
      EstateContactDocumentWorkflowService.evaluateAndPersist(
        userId,
        contactId,
        'legacy_contact' as ContactRole,
        countryCode,
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: legacyContactKeys.list(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: legacyContactKeys.workflow(userId, variables.contactId),
      });
    },
    onError: (error) => {
      logger.error('Failed to evaluate legacy contact doc workflow', {
        context: 'useEvaluateLegacyContactDocWorkflow',
        error: String(error),
      });
    },
  });
}

/**
 * Acknowledge a soft-block on a legacy contact's document workflow.
 */
export function useAcknowledgeLegacyContactSoftBlock() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: ({
      contactId,
      reason,
    }: {
      contactId: string;
      reason: string;
    }) =>
      EstateContactDocumentWorkflowService.acknowledgeSoftBlock(
        userId,
        contactId,
        reason,
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: legacyContactKeys.list(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: legacyContactKeys.workflow(userId, variables.contactId),
      });
    },
    onError: (error) => {
      logger.error('Failed to acknowledge soft block', {
        context: 'useAcknowledgeLegacyContactSoftBlock',
        error: String(error),
      });
    },
  });
}

export type { DocWorkflowStatusSnapshot };
